/* Copyright (c) 2021 Caring Transitions Online Auctions. All Rights Reserved. */

// library imports
import { Component } from 'react';
import { Button, FormControl, Modal } from 'react-bootstrap';
import { colorStringToColorArray } from 'doka';
import { DokaImageEditor } from 'react-doka';
import Axios from 'axios';

// project imports
import { getSellerUrl, getSellerHeaders } from 'shared/http';
import { IMAGE_TYPES } from 'shared/settings/config';
import { setEditorPlugins, getEditorDefaults, circleCropMask } from 'shared/doka';

import { blobToFile } from 'doka';
import { locationlist, locimageadd, locimagelist, locimageupdate } from 'shared/settings/apiconfig';
import toast from 'shared/toast';

// style imports
import 'doka/doka.css';
import './LocationImages.scss';
import { ArrayValueReorder } from 'shared/modifiers';
import heic2any from 'heic2any';

import FileType from "file-type/browser";
import { NoData } from 'features/components/redirect/NoData';

/* initializes doka editing plugins */
setEditorPlugins();

/**
 * this page is used to upload location's banner and profile
 * 
 * @param props
 * 
 * @return html code for uploading location's banner and profile
 * 
**/

export class LocationImages extends Component {

    locations;
    imagesData;
    profileEditorDefaults;
    bannerEditorDefaults;

    constructor(props) {
        super(props);

        this.state = {
            // selected location
            location: '',

            // banner image controls
            banner: {
                imgSrc: null,
                tempImg: null,
                modal: false
            },

            // profile image controls
            profile: {
                imgSrc: null,
                tempImg: null,
                modal: false
            },

            // loading
            pageLoading: true,
            errorpageredirect: false,
            errorType: ''
        };

        this.locations = [];
        this.imagesData = {};
        this.profileEditorDefaults = {
            imageCropAspectRatio: 1,
            cropEnableSelectPreset: false,
            imageCropMinSize: { width: 130, height: 130 },
            willRenderCanvas: (shapes, state) => circleCropMask(shapes, state)
        };
        this.bannerEditorDefaults = {
            imageCropAspectRatio: 29 / 5,
            imageBackgroundColor: colorStringToColorArray('#fff'),
            cropEnableButtonRotateRight: false,
            cropEnableButtonRotateLeft: false,
            cropEnableSelectPreset: false,
            imageCropMinSize: { width: 1200, height: 250 }
        };
    }

    componentDidMount() {
        this.getLocationList();
        window.history.pushState(null, null, window.location.pathname);
        window.addEventListener('popstate', this.onBack);
    }

    onBack = (e) => {
        e.preventDefault();
        window.history.pushState(null, null, window.location.pathname);
    }

    /* gets the location list for the user */
    getLocationList = async () => {
        await Axios.post(getSellerUrl(locationlist), JSON.stringify({ field: ['id', 'title'] }), getSellerHeaders())
            .then(response => {
                this.locations = ArrayValueReorder([...response.data['data']], 'title');
                this.setState({ location: this.locations[0] });

                this.getLocationImages();
            })
            .catch(error => {
                if(error.response.status == 401 || error.response.status == 404) {
                    this.setState({
                        errorpageredirect: true,
                        errorType: error.response.status == 401 ? 'expiryError' : error.response.status == 404 ? 'noData' : ''
                    })
                }
                this.setState({
                    pageLoading: false,
                })
                // toast.error(error.response.data['message']);
            });
    };

    /* gets the banner and profile for the selected location */
    getLocationImages = async () => {
        let filter = [{ field: 'locationid', value: Number(this.state.location['id']), op: '=', join: 'AND' }];
        await Axios.post(getSellerUrl(locimagelist), JSON.stringify({ filter: filter }), getSellerHeaders())
            .then(response => {
                ['banner', 'profile'].forEach(x => {
                    const index = response.data['data'].findIndex(y => y.name === x);
                    if (index !== -1) {
                        const element = response.data['data'][index];
                        this.setState({
                            [element['name']]: {
                                ...this.state[element['name']],
                                imgSrc: element['url']
                            }
                        });

                        this.imagesData[element['name']] = element['id'];
                    } else {
                        this.setState({
                            [x]: {
                                ...this.state[x],
                                imgSrc: null,
                                tempImg: null,
                                modal: false
                            }
                        });

                        delete this.imagesData[x];
                    }
                })
                response.data['data'].forEach(element => {

                });
            })
            .catch(error => {
                this.setState({
                    banner: {
                        imgSrc: null,
                        tempImg: null,
                        modal: false
                    },
                    profile: {
                        imgSrc: null,
                        tempImg: null,
                        modal: false
                    }
                });
                toast.error(error.response.data['message']);
            })
            .finally(() => {
                this.setState({ pageLoading: false });
            });
    };

    /* image editing functions for banner */
    onClickBannerFileInput = () => {
        document.getElementById('img-banner').click();
    };

    onUploadBanner = async(event) => {
        // convert image to base 64
        const file = new FileReader();
        let blob = null;

        // triggered when image is loaded
        file.onloadend = (fileEvent) => {
            // to get image property details from base 64
            const image = new Image();

            // tirggered when image is loaded
            image.onload = () => {
                if (image.width < 1200) {
                    this.bannerEditorDefaults['previewUpscale'] = true;
                    this.bannerEditorDefaults['cropAutoCenterImageSelectionTimeout'] = 1;
                }

                this.setState({
                    banner: {
                        ...this.state.banner,
                        tempImg: blob!=null ? URL.createObjectURL(blob)  : URL.createObjectURL(event.target.files[0]),
                        modal: true
                    }
                });

                // reset input to upload same image
                event.target.value = '';
            };

            /* load the base64 to get image height and width */
            image.src = fileEvent.target.result;
        };

        const mimetype = await FileType.fromBlob(event.target.files[0]);
            if (mimetype?.mime === "image/heic" && mimetype?.ext === 'heic') { 
                    blob = await heic2any({
                        blob: event.target.files[0],
                        toType: 'image/jpeg',
                        quality: 0.94,
                    });

                }
        /* load the image to get base64 */
        file.readAsDataURL(blob!=null ? blob : event.target.files[0]);
    };

    onSaveBanner = (output) => {
        this.setState({
            banner: {
                ...this.state.banner,
                imgSrc: URL.createObjectURL(output.dest),
                modal: false
            }
        });

        // convert doka processed image to base 64
        let render = new FileReader();

        // triggered when image is loaded
        render.onloadend = async () => {
            const data = {
                locationid: this.state.location['id'],
                name: 'banner',
                image: render.result.replace('data:', '').replace(/^.+,/, '')
            };

            await this.saveBannerImage(data);
        };

        render.readAsDataURL(output.dest);
    };

    saveBannerImage = async (data) => {
        // called when image is already uploaded
        if (this.imagesData.hasOwnProperty('banner')) {
            data['id'] = this.imagesData['banner'];
            await Axios.put(getSellerUrl(locimageupdate), JSON.stringify({ data: [data] }), getSellerHeaders())
                .then(response => {
                    toast.success(response.data['message']);
                })
                .catch(error => {
                    toast.error(error.response.data['message']);
                });
        } else {
            // called when there is no image uploaded
            await Axios.post(getSellerUrl(locimageadd), JSON.stringify({ data: [data] }), getSellerHeaders())
                .then(response => {
                    toast.success(response.data['message']);
                })
                .catch(error => {
                    toast.error(error.response.data['message']);
                });
        }
    };

    onCancelBanner = () => {
        this.setState({
            banner: {
                ...this.state.banner,
                modal: false,
                tempImg: null
            }
        });
    };

    /* image editing functions for profile */
    onClickProfileFileInput = () => {
        document.getElementById('img-profile').click();
    };

    onUploadProfile = async(event) => {
        let blob = null;
        const mimetype = await FileType.fromBlob(event.target.files[0]);
        if (mimetype?.mime === "image/heic" && mimetype?.ext === 'heic'){ 
            this.setState({
                profile: {
                    ...this.state.profile,
                    modal: true
                }
            })
            blob = await heic2any({
            blob: event.target.files[0],
            toType: 'image/jpeg',
            quality: 0.94,
        });

    }
        this.setState({
            profile: {
                ...this.state.profile,
                tempImg: blob!=null ? URL.createObjectURL(blob) : event.target.files[0],
                modal: true
            }
        });

        // reset input to upload same image
        event.target.value = '';
    };

    onSaveProfile = (output) => {
        this.setState({
            profile: {
                ...this.state.profile,
                imgSrc: URL.createObjectURL(output.dest),
                modal: false
            }
        });

        // convert doka processed image to base 64
        let render = new FileReader();

        // triggered when image is loaded
        render.onloadend = async () => {
            const data = {
                locationid: this.state.location['id'],
                name: 'profile',
                image: render.result.replace('data:', '').replace(/^.+,/, '')
            };

            await this.saveProfile(data);
        };

        render.readAsDataURL(output.dest);
    };

    saveProfile = async (data) => {
        // called when image is already uploaded
        if (this.imagesData.hasOwnProperty('profile')) {
            data['id'] = this.imagesData['profile'];
            Axios.put(getSellerUrl(locimageupdate), JSON.stringify({ data: [data] }), getSellerHeaders())
                .then(response => {
                    toast.success(response.data['message']);
                })
                .catch(error => {
                    toast.error(error.response.data['message']);
                });
        } else {
            // called when there is no image uploaded
            Axios.post(getSellerUrl(locimageadd), JSON.stringify({ data: [data] }), getSellerHeaders())
                .then(response => {
                    toast.success(response.data['message']);
                })
                .catch(error => {
                    toast.error(error.response.data['message']);
                });
        }
    };

    onCancelProfile = () => {
        this.setState({
            profile: {
                ...this.state.profile,
                modal: false,
                tempImg: null
            }
        });
    };

    /* get banner and profile when location is changed */
    changeLocation = (event) => {
        this.setState({ location: this.locations[event.target.value] }, () => this.getLocationImages());
    };

    render() {
        if (this.state.errorpageredirect) {
            return <NoData {...this.props} type={this.state.errorType} />
        }
        return (
            <div className="d-flex flex-column location-images">
                {
                    this.state.pageLoading ?
                        <div className="d-flex flex-column align-items-center justify-content-center w-100 h-100">
                            <img src={`${process.env.PUBLIC_URL}/static/icons/table/option_1.gif`} alt="Please wait ....." height="100px" width="100px" />
                        </div> :
                        <div className="d-flex flex-column location-image-layout">
                            <div className="d-flex flex-column location-select">
                                <FormControl as="select" id="location" name="location" defaultValue={this.state.location} placeholder=" "
                                    disabled={this.state.pageLoading} onChange={this.changeLocation} >
                                    {
                                        this.locations.map((element, index) => <option value={index} key={index}>{element['title']}</option>)
                                    }
                                </FormControl>
                            </div>
                            <div className="d-flex flex-column p-3 my-3 bg-steel w-100 border">
                                <div className="d-flex flex-column flex-sm-row justify-content-between">
                                    <div className="d-flex flex-column">
                                        <h6><b>Banner</b><span style={{ color: "#878787" }}> (Suggested image size 1200 &times; 250 pixels)</span></h6>
                                    </div>
                                    {
                                        this.state.banner.imgSrc ?
                                            <div className="d-flex flex-column flex-sm-row pt-3 pt-sm-0">
                                                <Button variant="secondary" onClick={this.onClickBannerFileInput}>Change Image</Button>
                                            </div> :
                                            null
                                    }
                                </div>
                                <div className="d-flex flex-row align-items-center justify-content-center mt-2 bg-white" 
                                    style={{ width: '100%', overflow: 'hidden', border: '2px dashed #c5c5c5' }}>
                                    <FormControl id="img-banner" type="file" onChange={this.onUploadBanner} accept={IMAGE_TYPES} hidden />
                                    {
                                        this.state.banner.imgSrc ?
                                            <img src={this.state.banner.imgSrc} alt="invalid banner" style={{ width: '100%', height: '15.35vw' }} /> :
                                            <div className="d-flex flex-column justify-content-center align-items-center w-100" style={{ height: '250px' }}>
                                                <i className=" fa fa-plus rounded-circle bg-secondary text-white p-3 clickable" onClick={this.onClickBannerFileInput}></i>
                                                <span className="text-secondary my-3" style={{ fontWeight: 600 }}>Add Banner Image</span>
                                            </div>
                                    }
                                </div>
                            </div>

                            <div className="d-flex flex-column p-3 my-3 bg-steel w-100 border">
                                <span><b>Profile</b></span>
                                <div className="d-flex flex-row justify-content-center pt-3 pt-sm-0">
                                    <div className="d-flex flex-column border bg-white" style={{ padding: '24px' }}>
                                        <div className="d-flex flex-row justify-content-center mb-3 profile-rounded">
                                            <FormControl id="img-profile" type="file" onChange={this.onUploadProfile} accept={IMAGE_TYPES} hidden />
                                            {
                                                this.state.profile.imgSrc ?
                                                    <img src={this.state.profile.imgSrc} alt="invalid profile" width="100%" height="100%" /> :
                                                    <div className="d-flex flex-row justify-content-center align-items-center w-100 h-100">
                                                        <img src={`${process.env.PUBLIC_URL}/static/icons/profile/default_2.svg`} alt="default" height="75" width="75" />
                                                    </div>
                                            }
                                        </div>
                                        <div className="d-flex flex-row justify-content-center w-100">
                                            <span className="text-secondary clickable" onClick={this.onClickProfileFileInput}>
                                                {
                                                    this.state.profile.imgSrc ? 'Change Image' : 'Add Image'
                                                }
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                }

                {/* dialog box for banner upload and edit */}
                <Modal show={this.state.banner.modal} size="lg" animation={false} onHide={() => this.setState({ ...this.state })}>
                    <Modal.Header>
                        <Button type="button" onClick={this.onCancelBanner} className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></Button>
                    </Modal.Header>
                    <Modal.Body className="doka">
                        <div className="doka-banner-layout">
                            <DokaImageEditor {...getEditorDefaults()}
                                {...this.bannerEditorDefaults} src={this.state.banner.tempImg}
                                onProcess={this.onSaveBanner} />
                        </div>
                    </Modal.Body>
                </Modal>

                {/* dialog box for profile upload and edit */}
                <Modal show={this.state.profile.modal} size="lg" animation={false} onHide={() => this.setState({ ...this.state })}>
                    <Modal.Header>
                        <Button type="button" onClick={this.onCancelProfile} className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></Button>
                    </Modal.Header>
                    <Modal.Body className="doka d-flex flex-column align-items-center justify-content-center">
                        <div className="doka-profile-layout" style={{ width: '100%' }}>
                            <DokaImageEditor {...getEditorDefaults({ width: 130, height: 130 })}
                                {...this.profileEditorDefaults} src={this.state.profile.tempImg}
                                onProcess={this.onSaveProfile} />
                        </div>
                    </Modal.Body>
                </Modal>
            </div>
        );
    }
}